let audioContext;
let instrumentPromise;

function reportState(state) {
    window.dispatchEvent(new CustomEvent('guitarcharts:audio-state', {detail: state}));
}

function getInstrument() {
    if (!instrumentPromise) {
        instrumentPromise = loadInstrument().catch(error => {
            instrumentPromise = undefined;
            throw error;
        });
    }
    return instrumentPromise;
}

async function loadInstrument() {
    const {Soundfont} = await import(/* webpackChunkName: "guitar-audio" */ 'smplr');
    const guitar = Soundfont(audioContext, {
        // Pin the existing MP3 samples: smplr otherwise prefers Ogg where supported.
        instrumentUrl: 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/acoustic_guitar_steel-mp3.js',
        // Use smplr's default volume, velocity and guitar gain.
        loadLoopData: false,
        storage: {
            async fetch(url) {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Guitar download failed: ${response.status}`);
                return response;
            },
        },
    });
    try {
        await guitar.ready;
        // smplr skips failed decodes and can substitute a neighbouring pitch.
        // Keep all 88 original samples, or allow the next click to retry.
        if (guitar.loadProgress.loaded !== 88) throw new Error('Incomplete guitar samples');
        return guitar;
    } catch (error) {
        guitar.dispose();
        throw error;
    }
}

async function getReadyInstrument() {
    audioContext ||= new (window.AudioContext || window.webkitAudioContext)();
    // Start resume inside the user gesture, before waiting for any network IO.
    // Safari can also enter "interrupted" after an audio interruption.
    const resumed = audioContext.state === 'running' ? Promise.resolve() : audioContext.resume();
    if (!instrumentPromise) reportState('loading');
    const [guitar] = await Promise.all([getInstrument(), resumed]);
    reportState('ready');
    return guitar;
}

// The chord game plays its first answer after a timer. Unlock audio while its
// Play button still has user activation, without playing a note early.
export async function prepareAudio() {
    try {
        await getReadyInstrument();
        return true;
    } catch (error) {
        reportState('error');
        console.error('Unable to prepare guitar audio', error);
        return false;
    }
}

async function play(midiNotes, spacing) {
    if (midiNotes.length === 0) return true;
    try {
        const guitar = await getReadyInstrument();
        const time = audioContext.currentTime;
        midiNotes.forEach((note, index) => guitar.start({note, time: time + spacing * index}));
        return true;
    } catch (error) {
        reportState('error');
        // Inline chart/game handlers do not await playback; never leave a rejected
        // promise unhandled. A later user gesture can retry loading or resuming.
        console.error('Unable to play guitar audio', error);
        return false;
    }
}

export function playMidiNotes(midiNotes) {
    return play(midiNotes, 0.4);
}

export function playMidiChord(midiNotes) {
    return play(midiNotes, 0);
}
